import React from 'react';
import BaseFactory from './BaseFactory';
import {
  getBlogAnchorTag,
  getFaqsAnchorTag,
  hasLocationHours,
  getLastVerifiedStr,
  getBrandName,
  getCityAnchorTag,
  getLocationDistanceInMiles,
  getClinicAnchorTag,
  hasBrandName,
  NoTopMarginParagraph,
  NoBottomMarginParagraph,
  hasCustomDescription,
} from './util';
import { PEDIATRIC_PATH } from '../../../constants';
import { isEmptyArray } from '../../../core/util/array';
import { BASE_URL } from '../../../config';
import CustomDescriptionSection from './CustomDescriptionSection';
import { SOLV_SECTION_TITLE } from './constants';

class NonPartnerPediatricUrgentCare extends BaseFactory {
  constructor(location, nearbyLocations, properties) {
    super(location, properties);
    this.nearbyLocations = nearbyLocations;
    this.lastVerifiedStr = getLastVerifiedStr(location);
    this.ratingCountStr =
      this.ratingCount > 0 ? `With more than ${this.ratingCount} reviews and ratings, ` : '';
    if (hasBrandName(nearbyLocations)) {
      const nearbyBrandLocation = nearbyLocations.brand.location;
      this.brandName = getBrandName(nearbyBrandLocation);
      this.nearbyBrandClinicCityAnchorTag = getCityAnchorTag(nearbyBrandLocation);
      this.nearbyBrandClinicDistance = getLocationDistanceInMiles(nearbyBrandLocation);
      this.sisterFacilityAnchorTag = getClinicAnchorTag(nearbyBrandLocation, 'sister facility');
    }
  }

  getDescription() {
    return (
      `${this.clinicName} is a pediatric or kids urgent care center ` +
      `located ${this.address}, ${this.city}, ` +
      `${this.state}, ${this.zip} providing immediate, non-life-threatening healthcare ` +
      `services to children and adolescent patients in the ${this.city} area. For more ` +
      `information, call ${this.clinicName}${this.phoneString}.`
    );
  }

  getKeywords() {
    return (
      `${this.clinicName}, ${this.clinicName}, ${this.city}, ${this.clinicName} ${this.zip}, ` +
      `pediatric urgent care ${this.city}, pediatric urgent care ${this.zip}, ` +
      `walk-in clinic ${this.city}, walk-in clinic ${this.zip}, ${this.city} ${this.state} ` +
      `pediatric clinic, ${this.zip} pediatric clinic`
    );
  }

  getBody() {
    return (
      <span>
        <CustomDescriptionSection location={this.location} />
        <NoTopMarginParagraph>
          {hasCustomDescription(this.location) && (
            <span>
              {SOLV_SECTION_TITLE}
              <br />
            </span>
          )}
          {this.clinicName} is a <b>pediatric urgent care center</b> serving the children,
          adolescents and young adults in {this.cityAnchorTag} with extended hours, making them a
          great option{' '}
          {hasLocationHours(this.location) && <span>{this.numDaysOpenString} a week </span>}
          when your child's primary care office may not be open. You can find this clinic located at{' '}
          {this.fullAddress}
          {hasLocationHours(this.location) && (
            <span>
              {' '}
              and patients can walk in to see them {this.nextOpenDayName} from{' '}
              {this.nextOpenDayHoursStr}
            </span>
          )}
          {this.lastVerifiedStr}.
        </NoTopMarginParagraph>
        {isEmptyArray(this.glowingWords) && this.rating && (
          <p>
            {this.ratingCountStr}
            <b>{this.clinicName}</b> has received an average of {this.rating} stars out of 5.
          </p>
        )}
        {!isEmptyArray(this.glowingWords) && !this.rating && (
          <p>
            {this.ratingCountStr}
            <b>{this.clinicName}</b> patients have used words like {this.glowingWordsString} to
            describe their experience. When your kiddo is not feeling well and when your regular{' '}
            <b>pediatrician</b> isn't available, you definitely want to look for positive sentiments
            from kids and parents who have gone before you.
          </p>
        )}
        {!isEmptyArray(this.glowingWords) && this.rating && (
          <p>
            {this.ratingCountStr}
            <b>{this.clinicName}</b> has received an average of {this.rating} stars out of 5 with
            patients using words like {this.glowingWordsString} to describe their experience. When
            your kiddo is not feeling well and when your regular <b>pediatrician</b> isn't
            available, you definitely want to look for positive sentiments from kids and parents who
            have gone before you.
          </p>
        )}
        <p>
          Nationally, most pediatric urgent care patients see average wait times between 15-30
          minutes before being seen by a provider, which should be expected at {this.clinicName} as
          well.
          {hasBrandName(this.nearbyLocations) && (
            <span>
              {' '}
              As part of the {this.brandName} group, they have a {this.sisterFacilityAnchorTag} in{' '}
              {this.nearbyBrandClinicCityAnchorTag} {this.nearbyBrandClinicDistance} miles away that
              you may want to consider if the wait gets too long.
            </span>
          )}
        </p>
        <NoBottomMarginParagraph>
          If you are looking to learn more about{' '}
          {this.isLinkable ? (
            <a href={`${BASE_URL}${PEDIATRIC_PATH}`}>pediatric urgent care</a>
          ) : (
            <span>pediatric urgent care</span>
          )}
          including statistics, trends, {getFaqsAnchorTag()} and other useful information, check out
          our {getBlogAnchorTag()} to empower your knowledge as a healthcare consumer.{' '}
          {this.cityGuideContent}
        </NoBottomMarginParagraph>
      </span>
    );
  }
}

export default NonPartnerPediatricUrgentCare;
